import React, { FC, memo, useCallback, useMemo, useEffect } from 'react';
import { Form } from 'antd';
import { useParams } from 'react-router-dom';
import { Input } from 'shared/ui/Input';
import { Select } from 'shared/ui/Select';
import { Button } from 'shared/ui/Button';

import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useGetAllWarehousesQuery } from 'entities/Warehouse';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { createZoneModalActions } from '../../model/slice/createZoneModalSlice';
import { stepperModalActions } from 'features/StepperModal';
import { getZoneBasics } from '../../model/selectors';

export const ZoneBasicsStep: FC = memo(() => {
  const { t } = useAppTranslation(['common']);
  const dispatch = useAppDispatch();
  const basics = useAppSelector(getZoneBasics);

  const [form] = Form.useForm();
  const { data: warehouses } = useGetAllWarehousesQuery();
  const { warehouseId: warehouseIdFromUrl } = useParams<{ warehouseId: string }>();

  const selectedWarehouse = warehouses?.find((warehouse) => warehouse.warehouseId === warehouseIdFromUrl);

  const warehouseOptions = useMemo(
    () => warehouses?.map((warehouse) => ({ label: warehouse.name, value: warehouse.warehouseId })) || [],
    [warehouses],
  );

  useEffect(() => {
    if (selectedWarehouse) {
      form.setFieldsValue({
        warehouseId: selectedWarehouse.warehouseId,
      });
    }
  }, [form, selectedWarehouse]);

  const next = useCallback(async () => {
    const values = await form.validateFields();

    if (!selectedWarehouse) {
      form.setFields([
        {
          name: 'warehouseId',
          errors: [t('Please, select warehouse')],
        },
      ]);
      return;
    }

    dispatch(
      createZoneModalActions.setBasics({
        name: values.name,
        warehouse: selectedWarehouse,
      }),
    );
    dispatch(stepperModalActions.changeActiveStepIndex(1));
  }, [dispatch, form, t, selectedWarehouse]);

  return (
    <div className="px-6 py-4">
      <Form layout="vertical" form={form} initialValues={{ name: basics?.name, warehouseId: basics?.warehouse?.warehouseId }}>
        <Form.Item label={t('Zone name')} name="name" rules={[{ required: true, message: t('Please, enter zone name') }]}>
          <Input placeholder={t('Please, enter zone name')} bordered />
        </Form.Item>

        <Form.Item label={t('Warehouse')} name="warehouseId" rules={[{ required: true, message: t('Please, select warehouse') }]}>
          <Select disabled options={warehouseOptions} placeholder={t('Please, select warehouse')} bordered />
        </Form.Item>

        <div className="flex justify-end">
          <Button onClick={next}>{t('Next', { ns: 'common' })}</Button>
        </div>
      </Form>
    </div>
  );
});
